import React from 'react';

const STORAGE_KEY_TIME_FORMAT = 'uptime_time_format';
const STORAGE_KEY_TIMEZONE = 'uptime_timezone';

// Default: 12-hour format (AM/PM)
const DEFAULT_TIME_FORMAT = '12';
const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Hook to manage time format and timezone settings
 * @returns {object} Object containing timeFormat, timezone, setTimeFormat, setTimezone
 */
export function useTimeSettings() {
	const [timeFormat, setTimeFormatState] = React.useState(() => {
		try {
			return localStorage.getItem(STORAGE_KEY_TIME_FORMAT) || DEFAULT_TIME_FORMAT;
		} catch (e) {
			return DEFAULT_TIME_FORMAT;
		}
	});

	const [timezone, setTimezoneState] = React.useState(() => {
		try {
			return localStorage.getItem(STORAGE_KEY_TIMEZONE) || DEFAULT_TIMEZONE;
		} catch (e) {
			return DEFAULT_TIMEZONE;
		}
	});

	const setTimeFormat = React.useCallback((format) => {
		try {
			localStorage.setItem(STORAGE_KEY_TIME_FORMAT, format);
			setTimeFormatState(format);
		} catch (e) {
			console.error('Failed to save time format:', e);
		}
	}, []);

	const setTimezone = React.useCallback((tz) => {
		try {
			localStorage.setItem(STORAGE_KEY_TIMEZONE, tz);
			setTimezoneState(tz);
		} catch (e) {
			console.error('Failed to save timezone:', e);
		}
	}, []);

	return {
		timeFormat,
		timezone,
		setTimeFormat,
		setTimezone,
	};
}

/**
 * Format time based on settings
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @param {string} timeFormat - '12' for AM/PM, '24' for 24-hour
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted time string
 */
export function formatTime(timestamp, timeFormat = DEFAULT_TIME_FORMAT, timezone = DEFAULT_TIMEZONE) {
	const date = new Date(timestamp);
	
	const options = {
		timeZone: timezone,
		hour: 'numeric',
		minute: '2-digit',
	};
	
	if (timeFormat === '12') {
		options.hour12 = true;
	} else {
		options.hour12 = false;
		options.hour = '2-digit';
	}
	
	return date.toLocaleTimeString('en-US', options);
}

/**
 * Format date string for tooltip (Today, Yesterday, or date)
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date string
 */
export function formatDateLabel(timestamp, timezone = DEFAULT_TIMEZONE) {
	const date = new Date(timestamp);
	const now = new Date();
	
	// Get date strings in the target timezone
	const dateStr = date.toLocaleDateString('en-US', { timeZone: timezone });
	const todayStr = now.toLocaleDateString('en-US', { timeZone: timezone });
	
	// Get yesterday in the target timezone
	const yesterday = new Date(now);
	yesterday.setDate(yesterday.getDate() - 1);
	const yesterdayStr = yesterday.toLocaleDateString('en-US', { timeZone: timezone });
	
	if (dateStr === todayStr) {
		return 'Today';
	} else if (dateStr === yesterdayStr) {
		return 'Yesterday';
	} else {
		// Get year components for comparison
		const dateYear = parseInt(date.toLocaleDateString('en-US', { timeZone: timezone, year: 'numeric' }));
		const todayYear = parseInt(now.toLocaleDateString('en-US', { timeZone: timezone, year: 'numeric' }));
		
		return date.toLocaleDateString('en-US', {
			timeZone: timezone,
			month: 'short',
			day: 'numeric',
			year: dateYear !== todayYear ? 'numeric' : undefined,
		});
	}
}
